import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { TranslationKey, useTranslation } from '../translations';

interface DatePickerProps {
  readonly date: string;
  readonly disabled?: boolean;
  readonly onChange: (date: string) => void;
}

function getDaysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

export function formatDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getFieldFromDate(field: 'day' | 'month' | 'year', date: string): number | null {
  const splitDate = date.split('-');
  if (splitDate.length !== 3) {
    return null;
  }

  const [year, month, day] = splitDate as [string, string, string];
  switch (field) {
    case 'day': {
      return Number.parseInt(day, 10);
    }

    case 'month': {
      return Number.parseInt(month, 10);
    }

    case 'year': {
      return Number.parseInt(year, 10);
    }
  }
}

export function getDefaultDate(): string {
  const date = new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000);
  return `${date.getFullYear()}-1-1`;
}

const monthLabels: TranslationKey[] = [
  'date_picker.month.jan',
  'date_picker.month.feb',
  'date_picker.month.mar',
  'date_picker.month.apr',
  'date_picker.month.may',
  'date_picker.month.jun',
  'date_picker.month.jul',
  'date_picker.month.aug',
  'date_picker.month.sep',
  'date_picker.month.oct',
  'date_picker.month.nov',
  'date_picker.month.dec',
];

export const DatePicker: FC<DatePickerProps> = ({ date, disabled, onChange }) => {
  const { ts } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState<number>(getFieldFromDate('month', date) || 1);
  const [selectedDay, setSelectedDay] = useState<number>(getFieldFromDate('day', date) || 1);
  const [selectedYear, setSelectedYear] = useState<number>(
    getFieldFromDate('year', date) || new Date().getFullYear() - 21,
  );

  const updateMonth = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedMonth(Number.parseInt(event.target.value, 10));
    },
    [setSelectedMonth],
  );

  const updateDay = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedDay(Number.parseInt(event.target.value, 10));
    },
    [setSelectedDay],
  );

  const updateYear = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedYear(Number.parseInt(event.target.value, 10));
    },
    [setSelectedYear],
  );

  useEffect(() => {
    setSelectedMonth(getFieldFromDate('month', date)!);
    setSelectedDay(getFieldFromDate('day', date)!);
    setSelectedYear(getFieldFromDate('year', date)!);
  }, [date]);

  useEffect(() => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    if (selectedDay > daysInMonth) {
      setSelectedDay(1);
    } else {
      const month = selectedMonth.toString().padStart(2, '0');
      const day = selectedDay.toString().padStart(2, '0');
      onChange(`${selectedYear}-${month}-${day}`);
    }
  }, [selectedMonth, selectedYear, selectedDay, onChange]);

  const yearArray = useMemo(() => {
    const year = new Date().getFullYear();
    return Array.from({ length: 120 }, (_, index) => year - index).filter((y) => y >= 1900);
  }, []);

  return (
    <Row>
      <Col lg={6}>
        <FormGroup>
          <Label for="date-month">{ts('common.month')}</Label>
          <Input
            defaultValue={selectedMonth}
            disabled={disabled}
            id="date-month"
            onChange={updateMonth}
            type="select"
          >
            {monthLabels.map((month, index) => (
              <option key={month} value={index + 1}>
                {ts(month)}
              </option>
            ))}
          </Input>
        </FormGroup>
      </Col>
      <Col lg={3}>
        <FormGroup>
          <Label for="date-day">{ts('common.day')}</Label>
          <Input
            defaultValue={selectedDay}
            disabled={disabled}
            id="date-day"
            onChange={updateDay}
            type="select"
          >
            {Array.from({ length: getDaysInMonth(selectedMonth, selectedYear) }).map((_, index) => (
              <option
                // eslint-disable-next-line react/no-array-index-key -- what the fuck else would I use?!
                key={index}
                selected={index + 1 === selectedDay}
                value={index + 1}
              >
                {index + 1}
              </option>
            ))}
          </Input>
        </FormGroup>
      </Col>
      <Col lg={3}>
        <FormGroup>
          <Label for="date-year">{ts('common.year')}</Label>
          <Input
            defaultValue={selectedYear}
            disabled={disabled}
            id="date-year"
            onChange={updateYear}
            type="select"
          >
            {yearArray.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Input>
        </FormGroup>
      </Col>
    </Row>
  );
};
